import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { WorkflowService } from '@core/services/workflow/workflow.service';
import {
  combineLatest,
  map,
  merge,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';
import {
  RequestWorkflowsData,
  WorkflowNodeExternal,
} from '@features/requests/request-form/components/request-form-workflows/types';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { InputPhotosService } from '@core/services/workflow/input-photos.service';
import { TuiLoader } from '@taiga-ui/core';
import { filter } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { StepStatus, WorkflowStep } from './workflow-stepper.types';

@Component({
  selector: 'x-workflow-stepper',
  imports: [
    NgClass,
    AsyncPipe,
    RouterLink,
    TuiLoader,
    NgTemplateOutlet,
    TranslatePipe,
  ],
  templateUrl: './workflow-stepper.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowStepperComponent {
  private readonly workflowService = inject(WorkflowService);
  private readonly inputPhotosService = inject(InputPhotosService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  public readonly $baseRoute = input<string>('', {
    alias: 'baseRoute',
  });

  public readonly $isFinalPage = input<boolean>(false, {
    alias: 'isFinalPage',
  });

  steps$: Observable<WorkflowStep[]> = merge(
    // Обновляем при изменении данных workflow
    toObservable(this.workflowService.$requestsWorkflowData),
    // Обновляем при навигации (роутинге)
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
      // Возвращаем текущие данные workflow при каждой навигации
      map(() => this.workflowService.$requestsWorkflowData()),
      startWith(this.workflowService.$requestsWorkflowData()),
    ),
  ).pipe(
    switchMap(wfData => {
      if (!wfData) return of([]);

      // Ноды всех регламентов по порядку (без мутации данных сервиса)
      const allNodes: Array<{
        node: WorkflowNodeExternal;
        workflowId: string;
      }> = wfData.requestsWorkflows.flatMap(workflow =>
        this.workflowService
          .getSortedNodes(workflow)
          .map(node => ({ node, workflowId: workflow.id })),
      );

      // Create observables for each node's status
      const stepObservables = allNodes.map(({ node, workflowId }, index) =>
        this.getNodeStatus$(node.id, workflowId).pipe(
          map(status => {
            const date = this.getNodeDate(node.id, wfData);

            // Находим первую форму для данной ноды
            const firstForm = wfData.workflowNodesForms
              .filter(form => form.workflowNodeId === node.id)
              .sort((a, b) => a.order - b.order)[0];

            return {
              nodeId: node.id,
              stageLabel: `${index + 1}`,
              task: node.title,
              date,
              status,
              isLast: false,
              pendingCount$: this.getNodePendingCount$(workflowId, node.id),
              workflowId,
              formId: firstForm?.id,
            };
          }),
        ),
      );

      if (stepObservables.length === 0) {
        return of([]);
      }

      // Combine all steps and add final "Завершить заявку" step
      return combineLatest(stepObservables).pipe(
        map(steps => {
          const areWorkflowsCompleted =
            this.workflowService.areWorkflowsCompleted();

          // Маркируем все шаги как submitted если workflow завершен
          const processedSteps = areWorkflowsCompleted
            ? this.markStepsAsSubmitted(steps)
            : steps;

          // Early return: на финальной странице не добавляем последний шаг
          if (this.$isFinalPage()) {
            return processedSteps;
          }

          // Пользователь не имеет права закрывать заявку поэтому не добавляем последний шаг
          if (!this.canComplete()) {
            return processedSteps;
          }

          // Добавляем финальный шаг "Завершить заявку"
          const finalStepStatus = this.getFinalStepStatus(
            steps,
            areWorkflowsCompleted,
          );

          return [...processedSteps, this.createFinalStep(finalStepStatus)];
        }),
      );
    }),
  );

  private canComplete(): boolean {
    return this.workflowService.canCompleteRequestFromWorkflows();
  }

  /**
   * Создает объект финального шага "Завершить заявку"
   */
  private createFinalStep(status: StepStatus): WorkflowStep {
    return {
      nodeId: -1,
      stageLabel: '',
      task: '',
      date: '',
      status,
      isLast: true,
    };
  }

  /**
   * Маркирует все шаги как submitted
   */
  private markStepsAsSubmitted(steps: WorkflowStep[]): WorkflowStep[] {
    return steps.map(step => ({ ...step, status: 'submitted' as StepStatus }));
  }

  /**
   * Определяет статус финального шага на основе состояния всех шагов
   */
  private getFinalStepStatus(
    steps: WorkflowStep[],
    areWorkflowsCompleted: boolean,
  ): StepStatus {
    if (areWorkflowsCompleted) return 'submitted';

    const allStepsSubmitted = steps.every(step => step.status === 'submitted');

    return allStepsSubmitted ? 'in-progress' : 'not-started';
  }

  private getNodePendingCount$(
    workflowId: string,
    nodeId: number,
  ): Observable<number> {
    return combineLatest([
      this.inputPhotosService.getPendingCountForNode$(workflowId, nodeId),
      this.workflowService.getBackgroundSyncPendingCountForNode$(nodeId),
    ]).pipe(
      map(([photosPending, formsPending]) => photosPending + formsPending),
    );
  }

  private getNodeStatus$(
    nodeId: number,
    workflowId: string,
  ): Observable<StepStatus> {
    return combineLatest({
      allCompleted: this.workflowService.allNodeFormsCompleted$(
        nodeId,
        workflowId,
      ),
      someInvalid: this.workflowService.someNodeFormsAreInvalid$(
        nodeId,
        workflowId,
      ),
      isInProgress: this.workflowService.nodeIsInProgress$(nodeId),
    }).pipe(
      map(({ allCompleted, someInvalid, isInProgress }) => {
        // Проверяем, является ли эта нода текущей активной
        const currentUrl = this.router.url;
        const isCurrentNode = currentUrl.includes(
          `/workflows/${workflowId}/${nodeId}/`,
        );

        if (allCompleted) return 'submitted';
        if (someInvalid) return 'submitted-invalid';

        // Если это текущая нода или она в прогрессе - показываем in-progress
        if (isCurrentNode || isInProgress) {
          return 'in-progress';
        }

        return 'not-started';
      }),
    );
  }

  private getNodeDate(nodeId: number, wfData: RequestWorkflowsData): string {
    // Find the latest submission for this node
    const nodeFormIds = new Set(
      wfData.workflowNodesForms
        .filter(form => form.workflowNodeId === nodeId)
        .map(form => form.id),
    );

    const nodeSubmissions = wfData.submissions
      .filter(sub => nodeFormIds.has(sub.workflowNodeFormId))
      .sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );

    if (nodeSubmissions.length === 0) return '';

    const latestSubmission = nodeSubmissions[0];
    const date = new Date(latestSubmission.updatedAt);

    const dateStr = date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
    });

    const timeStr = date.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${dateStr}, ${timeStr}`;
  }
}
